import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LogOut, Inbox, LayoutDashboard, Plus, Flag } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AdminNav() {
  const { user, logout } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [openErrorCount, setOpenErrorCount] = useState(0);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const loadCount = async () => {
    try {
      const r = await api.get("/admin/reviews/pending-count");
      setPendingCount(r.data.count || 0);
    } catch { /* not logged in or backend down */ }
    try {
      const r2 = await api.get("/admin/error-reports/open-count");
      setOpenErrorCount(r2.data.count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    loadCount();
    const t = setInterval(loadCount, 60_000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <header className="bg-white border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="font-display text-lg font-bold text-[hsl(var(--primary))]" data-testid="admin-logo-link">
            WaterfallFinder
          </Link>
          <span className="text-stone-400 text-sm">Admin</span>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="admin-menu-toggle"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-stone-100"
          >
            <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-stone-700 font-semibold text-sm">
              {(user?.email || "A")[0]?.toUpperCase()}
            </div>
            <span className="text-sm font-medium text-stone-700 hidden md:inline">{user?.email}</span>
            {(pendingCount + openErrorCount) > 0 && (
              <span
                data-testid="admin-pending-badge"
                className="bg-red-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                title={`${pendingCount} pending review${pendingCount === 1 ? "" : "s"} · ${openErrorCount} open error report${openErrorCount === 1 ? "" : "s"}`}
              >
                {pendingCount + openErrorCount}
              </span>
            )}
            <ChevronDown className="w-4 h-4 text-stone-500" />
          </button>
          {open && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-stone-200 p-2 text-sm z-50">
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                data-testid="admin-menu-dashboard"
                className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-stone-900"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <Link
                to="/admin/waterfalls/new"
                onClick={() => setOpen(false)}
                data-testid="admin-menu-add"
                className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-stone-900"
              >
                <Plus className="w-4 h-4" /> Add waterfall
              </Link>
              <Link
                to="/admin/reviews"
                onClick={() => setOpen(false)}
                data-testid="admin-menu-reviews"
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-stone-900"
              >
                <span className="flex items-center gap-2">
                  <Inbox className="w-4 h-4" /> Approve reviews
                </span>
                {pendingCount > 0 && (
                  <span className="bg-red-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </Link>
              <Link
                to="/admin/error-reports"
                onClick={() => setOpen(false)}
                data-testid="admin-menu-error-reports"
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-stone-900"
              >
                <span className="flex items-center gap-2">
                  <Flag className="w-4 h-4" /> Error reports
                </span>
                {openErrorCount > 0 && (
                  <span className="bg-rose-600 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                    {openErrorCount}
                  </span>
                )}
              </Link>
              <div className="my-1 border-t border-stone-100" />
              <button
                onClick={() => { setOpen(false); logout(); }}
                data-testid="admin-menu-logout"
                className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-stone-900"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
